import {
  ACESFilmicToneMapping,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import {
  CAMERA_DISTANCE,
  MAX_CAMERA_DISTANCE,
  MIN_CAMERA_DISTANCE,
} from "./constants";

export interface SceneContext {
  canvas: HTMLCanvasElement;
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  controls: OrbitControls;
  dispose(): void;
  onResize(cb: () => void): void;
}

export function createScene(canvas: HTMLCanvasElement): SceneContext {
  const renderer = new WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,  // lets the CSS body bg show through for theme swap
    premultipliedAlpha: false,
    powerPreference: "high-performance",
  });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.32;

  const scene = new Scene();

  const camera = new PerspectiveCamera(
    38,
    window.innerWidth / window.innerHeight,
    0.01,
    500,
  );
  camera.position.set(0, 0, CAMERA_DISTANCE);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.rotateSpeed = 0.5;
  controls.zoomSpeed = 0.8;
  controls.enablePan = false;
  controls.minDistance = MIN_CAMERA_DISTANCE;
  controls.maxDistance = MAX_CAMERA_DISTANCE;

  const resizeListeners: Array<() => void> = [];
  const handleResize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    for (const l of resizeListeners) l();
  };
  window.addEventListener("resize", handleResize);

  return {
    canvas,
    scene,
    camera,
    renderer,
    controls,
    dispose() {
      window.removeEventListener("resize", handleResize);
      controls.dispose();
      renderer.dispose();
    },
    onResize(cb) {
      resizeListeners.push(cb);
    },
  };
}
